import React, { useState } from 'react';
import styles from './DarCobradoModal.module.css';
import Button from '../ui/button';
import Input from '../ui/input';
import { showErrorToast, showSuccessToast } from '../../utils/notifications';
import { MyIcons } from '../icons/my-icons';
import { darFacturaCobrado } from '../../services/apiService';
import ConfirmacionModal from './ConfirmacionModal';

/**
 * Modal para marcar facturas como cobradas
 * Props:
 * - isOpen: boolean (si el modal está visible)
 * - onClose: función para cerrar el modal
 */
export default function DarCobradoModal({ 
  isOpen, 
  onClose
}) {
  const [numFactura, setNumFactura] = useState('');
  const [fechaCobro, setFechaCobro] = useState('');
  const [marcarGiro, setMarcarGiro] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Estados para el modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  const handleClose = () => {
    // Reset form
    setNumFactura('');
    setFechaCobro('');
    setMarcarGiro(false);
    setShowConfirmModal(false);
    setConfirmData(null);
    onClose();
  };

  const handleSubmit = async (e, confirmar = false) => {
    if (e) {
      e.preventDefault();
    }

    // Validaciones
    if (!numFactura.trim()) {
      showErrorToast('Por favor, ingresa al menos un número de factura');
      return;
    }

    if (!fechaCobro) {
      showErrorToast('Por favor, selecciona la fecha de cobro');
      return;
    }

    try {
      setLoading(true);

      // Llamar a la API
      const response = await darFacturaCobrado(numFactura.trim(), fechaCobro, confirmar, marcarGiro);

      // Verificar si necesita confirmación
      if (response.needsConfirmation && !confirmar) {
        console.log('Response completa:', JSON.stringify(response, null, 2));
        
        // Extraer todos los campos de alerta dinámicamente (excepto needsConfirmation, message, facturas y total)
        const alertFields = {};
        Object.keys(response).forEach(key => {
          if (key !== 'needsConfirmation' && key !== 'message' && key !== 'facturas' && key !== 'total' && key !== 'totalImporteFacturas') {
            if (Array.isArray(response[key]) && response[key].length > 0) {
              alertFields[key] = response[key];
            }
          }
        });
        
        console.log('Campos de alerta extraídos:', alertFields);
        
        // Agregar facturas formateadas para revisión
        const facturasFormateadas = response.facturas.map(f => 
          `${f.numFactura} - ${parseFloat(f.costeFinal).toFixed(2)}€`
        );
        
        setConfirmData({
          message: response.message || 'Hay avisos antes de marcar facturas como cobradas. Clica en confirmar si quieres continuar.',
          alertFields: {
            ...alertFields,
            'Facturas a marcar como cobradas': facturasFormateadas
          },
          totalImporteFacturas: response.totalImporteFacturas
        });
        setShowConfirmModal(true);
        setLoading(false);
        return;
      }

      showSuccessToast('Factura(s) marcada(s) como cobrada(s) correctamente');
      // No cerrar el modal automáticamente
      // Resetear solo los campos del formulario
      setNumFactura('');
      setFechaCobro('');
      setMarcarGiro(false);
      setShowConfirmModal(false);
      setConfirmData(null);
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      
      // Verificar si el error es un 409 con needsConfirmation
      if (error.response && error.response.status === 409 && error.response.data) {
        const errorData = error.response.data;
        console.log('Error 409 - Response completa:', JSON.stringify(errorData, null, 2));
        
        if (errorData.needsConfirmation && !confirmar) {
          // Extraer todos los campos de alerta dinámicamente
          const alertFields = {};
          Object.keys(errorData).forEach(key => {
            if (key !== 'needsConfirmation' && key !== 'message' && key !== 'facturas' && key !== 'total' && key !== 'totalImporteFacturas') {
              if (Array.isArray(errorData[key]) && errorData[key].length > 0) {
                alertFields[key] = errorData[key];
              }
            }
          });
          
          console.log('Campos de alerta extraídos del error 409:', alertFields);
          
          // Agregar facturas formateadas para revisión
          const facturasFormateadas = errorData.facturas ? errorData.facturas.map(f => 
            `${f.numFactura} - ${parseFloat(f.costeFinal).toFixed(2)}€`
          ) : [];
          
          setConfirmData({
            message: errorData.message || 'Hay avisos antes de marcar facturas como cobradas. Clica en confirmar si quieres continuar.',
            alertFields: {
              ...alertFields,
              ...(facturasFormateadas.length > 0 && { 'Facturas a marcar como cobradas': facturasFormateadas })
            },
            totalImporteFacturas: errorData.totalImporteFacturas
          });
          setShowConfirmModal(true);
          setLoading(false);
          return;
        }
      }
      
      showErrorToast(error.message || 'Error al marcar la(s) factura(s) como cobrada(s)');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    handleSubmit(null, true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2 className={styles.title}>Dar por Cobrado</h2>
            <button
              className={styles.closeButton}
              onClick={handleClose}
              type="button"
            >
              <MyIcons name="close" width={20} height={20} color="var(--gray)" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Número(s) de Factura <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={numFactura}
                onChange={(e) => setNumFactura(e.target.value)}
                placeholder="numFactura1, numFactura2, numFactura3"
                disabled={loading}
                required
              />
              <span className={styles.hint}>
                Puedes ingresar varios números de factura separados por comas
              </span>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Fecha de Cobro <span className={styles.required}>*</span>
              </label>
              <Input
                type="date"
                value={fechaCobro}
                onChange={(e) => setFechaCobro(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={marcarGiro}
                  onChange={(e) => setMarcarGiro(e.target.checked)}
                  disabled={loading}
                />
                Marcar como Giro
              </label>
            </div>

            <div className={styles.buttonGroup}>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                CANCELAR
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'GUARDANDO...' : 'GUARDAR'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmacionModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setLoading(false);
        }}
        onConfirm={handleConfirm}
        message={confirmData?.message}
        alertFields={confirmData?.alertFields}
        numFactura={numFactura}
        fecha={fechaCobro}
        onNumFacturaChange={setNumFactura}
        onFechaChange={setFechaCobro}
        loading={loading}
        showNumFacturaAdmin={false}
        totalImporteFacturas={confirmData?.totalImporteFacturas}
      />
    </>
  );
}
